import { defaultPopupSuspendSeconds } from '@coorum-research/shared-common/types/popup.type'
import { getResearchSettings } from './user-session-storage.util'
import { getStorageItem, setStorageItem } from './storage.util'

// ポップアップのバツボタンを押した際にしばらく表示しない機能のストレージ
const suspendStorageKeyPrefix = 'coorum-research-suspend'

export type SuspendConfig = {
  id: string
  suspendSeconds: number | null
}

type SuspendRecord = {
  id: string
  suspendedAt?: string
  suspendedUntil?: string
}

function generateStorageKey() {
  const { accountId, memberId } = getResearchSettings()
  return [suspendStorageKeyPrefix, accountId, memberId].join(':')
}

export function getDefaultSuspendSeconds(): number {
  const envSeconds = import.meta.env.VITE_POPUP_SUSPEND_DEFAULT_SECONDS
  return Number.isInteger(+envSeconds) ? +envSeconds : defaultPopupSuspendSeconds
}

function getSuspendedAt(record: SuspendRecord): Date | undefined {
  if (record.suspendedAt) {
    return new Date(record.suspendedAt)
  } else if (record.suspendedUntil) { // TODO: suspendedUtilはsuspendedAt版のリリースの後削除して良い
    const suspendedUntil = new Date(record.suspendedUntil)
    return new Date(suspendedUntil.getTime() - getDefaultSuspendSeconds() * 1000)
  }

  return
}

export function getSuspendedUntil(record: SuspendRecord, config: SuspendConfig): Date | undefined {
  const suspendedAt = getSuspendedAt(record)
  if (!suspendedAt) {
    return
  }

  const suspendSeconds = config.suspendSeconds ?? getDefaultSuspendSeconds()

  return new Date(suspendedAt.getTime() + suspendSeconds * 1000)
}

export function getSuspendingRecords(configs: SuspendConfig[], now: Date = new Date()) {
  const records = getSuspendRecords()
  if (!records || records.length === 0) {
    return []
  }

  const { suspending } = records.reduce<Record<'suspending' | 'expired', SuspendRecord[]>>(
    (result, record) => {
      const config = configs.find(({ id }) => record.id === id)
      // 既に有効なpopupではない場合はexpired
      if (!config) {
        result.expired.push(record)
        return result
      }

      const suspendedUntil = getSuspendedUntil(record, config)
      // 停止時間が見つからない場合はexpired
      if (!suspendedUntil || Number.isNaN(suspendedUntil.getTime())) {
        result.expired.push(record)
        return result
      }

      if (now.getTime() < suspendedUntil.getTime()) {
        result.suspending.push({
          id: record.id,
          suspendedAt: getSuspendedAt(record)?.toISOString(),
        })
      } else {
        result.expired.push(record)
      }
      return result
    },
    { suspending: [], expired: [] },
  )
  setSuspendRecords(suspending)

  return suspending
}

export function getSuspendRecords() {
  const key = generateStorageKey()
  const value = getStorageItem<SuspendRecord[]>(key)
  if (!value) {
    return []
  }
  return value
}

export function setSuspendRecords(records: SuspendRecord[]) {
  const key = generateStorageKey()
  setStorageItem<SuspendRecord[]>(key, records)
}

export function setSuspendRecord(record: SuspendRecord) {
  const key = generateStorageKey()
  const existRecords = getSuspendRecords() ?? []
  const records = [...existRecords, record]
  setStorageItem<SuspendRecord[]>(key, records)
}
